import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { computeSlaState, formatBizDuration } from '@/lib/slaClock';
import { Clock, AlertTriangle, Shield, Siren } from 'lucide-react';

/**
 * Live SLA countdown chip for new_lead cards. Ticks every 60s.
 * Renders nothing if SLA doesn't apply (old lead, or stage != new_lead).
 */
export const SlaCountdownChip = ({ lead, compact = false }) => {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const state = React.useMemo(() => computeSlaState(lead, new Date()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lead, tick]);
  if (!state.applies) return null;

  let color, Icon, text, tooltip;
  const elapsed = formatBizDuration(state.elapsedBizHours);
  switch (state.level) {
    case 'breached':
      color = 'bg-red-600 text-white animate-pulse hover:bg-red-600';
      Icon = Siren;
      text = compact ? 'BREACH' : 'SLA BREACH';
      tooltip = state.reassigned
        ? `Reassigned by SLA (12h no-contact).`
        : `Past 12 biz-hours with no contact — reassignment on next sweep.`;
      break;
    case 'approaching_reassign':
      color = 'bg-orange-600 text-white hover:bg-orange-600';
      Icon = AlertTriangle;
      text = `⚠ Reassign ${formatBizDuration(state.remainingToReassign)}`;
      tooltip = `Warned already · reassignment in ${formatBizDuration(state.remainingToReassign)} of business hours.`;
      break;
    case 'warned':
      color = 'bg-amber-500 text-white hover:bg-amber-500';
      Icon = AlertTriangle;
      text = compact ? 'Warned' : `⚠ Reassign ${formatBizDuration(state.remainingToReassign)}`;
      tooltip = `3h SLA warning already sent. ${formatBizDuration(state.remainingToReassign)} biz-hrs until auto-reassign.`;
      break;
    case 'approaching_warn':
      color = 'bg-yellow-400 text-yellow-900 hover:bg-yellow-400';
      Icon = Clock;
      text = `Warn in ${formatBizDuration(state.remainingToWarn)}`;
      tooltip = `SLA warning fires in ${formatBizDuration(state.remainingToWarn)} of business hours if no contact.`;
      break;
    default:
      color = 'bg-emerald-500 text-white hover:bg-emerald-500';
      Icon = Shield;
      text = compact ? 'SLA OK' : `SLA ${formatBizDuration(state.remainingToWarn)} left`;
      tooltip = `${elapsed} elapsed · ${formatBizDuration(state.remainingToWarn)} until 3h warn threshold.`;
      break;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            className={`text-[10px] font-semibold ${color} cursor-help`}
            data-testid={`sla-chip-${lead.id}`}
          >
            <Icon className="h-3 w-3 mr-1" />
            {text}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          <div className="font-semibold mb-1">SLA · {elapsed} elapsed</div>
          <div className="text-muted-foreground">{tooltip}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default SlaCountdownChip;
